import { Injectable, inject, signal } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, take, tap } from 'rxjs/operators';

import { FlowToken, FlowApi } from '@venecia/apis/auth';
import { StorageService } from '@venecia/services';
import { GlobalConstants } from '@venecia/constants';
import { BearerUtil } from '@venecia/utils';

@Injectable({ providedIn: 'root' })
export class AuthFacadeService {
    private readonly tokenApi = inject(FlowApi);
    private readonly storage = inject(StorageService);

    /**
     * Holds an in-flight token request so multiple concurrent callers share it.
     * Must be cleared on completion/error.
    */
    private inFlightToken$: Observable<string> | null = null;

    flowCode = signal<string>('');

    /**
     * Returns a valid access token.
     * - Uses sessionStorage if token is valid
     * - Otherwise fetches a new token (single-flight)
     */
    getValidAccessToken$(): Observable<string> {
        const current: FlowToken | null = this.storage.get<FlowToken>(GlobalConstants.FLOW_BEARER_KEY);

        if (BearerUtil.isValid(current)) {
            const storagedCode = this.storage.get<string>(GlobalConstants.FLOW_CODE);
            if (storagedCode === this.flowCode()) {
                return of(current!['access-token']);
            }
        }

        return this.fetchAccessTokenSingleFlight$();
    }

    /**
     * Performs full bootstrap
     */
    bootstrap$(): Observable<string> {
        return this.getValidAccessToken$();
    }

    private fetchAccessTokenSingleFlight$(): Observable<string> {
        if (this.inFlightToken$) {
            return this.inFlightToken$;
        }

        this.inFlightToken$ = this.tokenApi.getFlowToken(this.flowCode())
            .pipe(
                take(1),
                map((resp) => resp.body ?? null),
                map((token: FlowToken | null) => {
                    if (!BearerUtil.isValid(token)) {
                        BearerUtil.throwHttpError();
                    }
                    return token!;
                }),
                tap((token) => {
                    this.storage.set<FlowToken>(GlobalConstants.FLOW_BEARER_KEY, token);
                    this.storage.set<string>(GlobalConstants.FLOW_CODE, this.flowCode());
                }
                ),
                map((token) => token['access-token']),
                shareReplay({ bufferSize: 1, refCount: false }),
                finalize(() => {
                    this.inFlightToken$ = null;
                }),
                catchError((err) => {
                    this.inFlightToken$ = null;
                    return throwError(() => err);
                })
            );

        return this.inFlightToken$;
    }
}
